import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Sparkles, Clock, X, Calendar } from "lucide-react";

const BLANK = {
  tag: "", title: "", line: "", cta: "Book Now",
  slug: "", color: "#D4AF37", order: 0, active: true,
  end_at: "", start_at: "",
};

// Season templates — quick-fill for common seasonal campaigns
const nowPlusDays = (days, hour = 9) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  return d;
};
const setDate = (month0, day, hour = 9) => {
  // Return a Date on the NEXT occurrence of month0/day (year-aware)
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  const attempt = new Date(d.getFullYear(), month0, day, hour, 0, 0, 0);
  if (attempt.getTime() < d.getTime()) attempt.setFullYear(d.getFullYear() + 1);
  return attempt;
};
const dtLocal = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const SEASON_TEMPLATES = [
  { key: "diwali",     label: "Diwali Sale",     tag: "Diwali Special",  color: "#EA580C",
    line: "Flat 15% off · Coupon DIWALI15", cta: "Grab Diwali Deal",
    start: () => setDate(9, 10),   // Oct 10 next
    end: () => setDate(10, 5, 23) },
  { key: "christmas",  label: "Christmas & New Year", tag: "Winter Escape",  color: "#059669",
    line: "Extra ₹2,000 off · Coupon PACKGO500", cta: "Book Winter Trip",
    start: () => setDate(11, 15),
    end: () => setDate(0, 5, 23) }, // Jan 5
  { key: "summer",     label: "Summer Cool-off",  tag: "Summer Escape",    color: "#0EA5E9",
    line: "Hill stations from ₹9,999", cta: "Beat the Heat",
    start: () => setDate(3, 1),
    end: () => setDate(5, 15, 23) },
  { key: "monsoon",    label: "Monsoon Magic",    tag: "Monsoon Special",  color: "#7C3AED",
    line: "Kerala & Meghalaya · Save up to 20%", cta: "Explore Monsoon",
    start: () => setDate(6, 1),
    end: () => setDate(8, 15, 23) },
  { key: "weekend",    label: "Flash Weekend",    tag: "Flash Deal",       color: "#DC2626",
    line: "48-hour flash · Save 10% with WELCOME10", cta: "Grab Fast",
    start: () => nowPlusDays(0),
    end: () => nowPlusDays(2, 23) },
  { key: "republic",   label: "Republic Day",     tag: "Republic Sale",    color: "#D4AF37",
    line: "26% off (up to ₹5,000)", cta: "Save Now",
    start: () => setDate(0, 20),
    end: () => setDate(0, 28, 23) },
];

// datetime-local <-> ISO helpers
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toIso = (local) => (local ? new Date(local).toISOString() : null);

export default function AdminDeals() {
  const [deals, setDeals] = useState([]);
  const [editing, setEditing] = useState(null); // null or deal object
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const now = Date.now();
  const statusOf = (d) => {
    if (!d.active) return "paused";
    const s = d.start_at ? new Date(d.start_at).getTime() : 0;
    const e = d.end_at ? new Date(d.end_at).getTime() : Infinity;
    if (now < s) return "upcoming";
    if (now >= e) return "expired";
    return "live";
  };

  const filtered = useMemo(() => {
    if (filter === "all") return deals;
    return deals.filter((d) => statusOf(d) === filter);
  }, [deals, filter, now]);

  const counts = useMemo(() => {
    const c = { all: deals.length, live: 0, upcoming: 0, expired: 0, paused: 0 };
    deals.forEach((d) => { c[statusOf(d)]++; });
    return c;
  }, [deals, now]);

  const refresh = () =>
    api.get("/admin/deals").then((r) => setDeals(r.data.deals || []));

  useEffect(() => { refresh(); }, []);

  const open = (deal) => setEditing(
    deal
      ? { ...deal, start_at: toLocalInput(deal.start_at), end_at: toLocalInput(deal.end_at) }
      : { ...BLANK }
  );

  const applyTemplate = (tpl) => {
    setEditing({
      ...BLANK,
      tag: tpl.tag,
      title: `${tpl.label} — set your destination`,
      line: tpl.line,
      cta: tpl.cta,
      color: tpl.color,
      slug: "",
      active: true,
      start_at: dtLocal(tpl.start()),
      end_at: dtLocal(tpl.end()),
    });
  };
  const close = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...editing,
        order: +editing.order || 0,
        start_at: toIso(editing.start_at),
        end_at: toIso(editing.end_at),
      };
      delete body.id;
      delete body.created_at;
      if (editing.id) {
        await api.put(`/admin/deals/${editing.id}`, body);
        toast.success("Deal updated");
      } else {
        await api.post("/admin/deals", body);
        toast.success("Deal created");
      }
      await refresh();
      close();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const del = async (deal) => {
    if (!window.confirm(`Delete "${deal.title}"?`)) return;
    try {
      await api.delete(`/admin/deals/${deal.id}`);
      toast.success("Deleted");
      await refresh();
    } catch (e) { toast.error("Delete failed"); }
  };

  const setF = (k) => (e) => setEditing({ ...editing, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div className="text-sm text-slate-500">{deals.length} deal{deals.length !== 1 && "s"} configured</div>
        <button data-testid="admin-new-deal" onClick={() => open(null)}
          className="px-4 py-2 rounded-full btn-gold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4"/> New Deal
        </button>
      </div>

      {/* Season Templates */}
      <div className="mb-6 rounded-2xl p-5 bg-gradient-to-br from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4 text-[#D4AF37]"/>
          <span className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">Season Templates</span>
          <span className="text-xs text-slate-500">— one-click seasonal campaigns pre-scheduled for you</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {SEASON_TEMPLATES.map((t) => (
            <button key={t.key} data-testid={`season-${t.key}`} onClick={() => applyTemplate(t)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border-2 hover:scale-105"
              style={{ borderColor: t.color, color: t.color }}>
              + {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Status filter tabs */}
      <div data-testid="deal-status-tabs" className="mb-6 flex flex-wrap gap-2">
        {[
          {k:"all", l:"All"},
          {k:"live", l:"Live now"},
          {k:"upcoming", l:"Scheduled"},
          {k:"expired", l:"Expired"},
          {k:"paused", l:"Paused"},
        ].map((t) => (
          <button key={t.k} data-testid={`filter-${t.k}`} onClick={() => setFilter(t.k)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
              filter === t.k
                ? "btn-gold"
                : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300"
            }`}
          >{t.l} <span className="opacity-60">· {counts[t.k]}</span></button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {filtered.map((d) => {
          const st = statusOf(d);
          const ends = d.end_at ? new Date(d.end_at) : null;
          const starts = d.start_at ? new Date(d.start_at) : null;
          const badgeStyles = {
            live: "bg-emerald-500/10 text-emerald-600",
            upcoming: "bg-blue-500/10 text-blue-600",
            expired: "bg-red-500/10 text-red-600",
            paused: "bg-slate-500/10 text-slate-500",
          };
          return (
            <div key={d.id} data-testid={`admin-deal-${d.slug}`}
              className={`rounded-2xl p-5 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 ${
                st === "expired" || st === "paused" ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold"
                  style={{ backgroundColor: d.color + "22", color: d.color }}>
                  <Sparkles className="w-3 h-3"/> {d.tag}
                </span>
                <div className="flex gap-2">
                  <button data-testid={`admin-edit-deal-${d.slug}`} onClick={() => open(d)}
                    className="w-8 h-8 rounded-full hover:bg-slate-100 dark:hover:bg-white/5 flex items-center justify-center">
                    <Pencil className="w-3.5 h-3.5"/>
                  </button>
                  <button data-testid={`admin-delete-deal-${d.slug}`} onClick={() => del(d)}
                    className="w-8 h-8 rounded-full text-red-500 hover:bg-red-500/10 flex items-center justify-center">
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>
              </div>
              <div className="font-bold text-blue-950 dark:text-white">{d.title}</div>
              <div className="text-sm text-[#D4AF37] font-semibold mt-1">{d.line}</div>
              <div className="text-xs text-slate-500 mt-2">→ /packages/{d.slug} · CTA: {d.cta}</div>
              <div className="flex items-center gap-3 mt-3 text-xs flex-wrap">
                <span data-testid={`deal-status-${d.slug}`} className={`px-2 py-0.5 rounded-full uppercase tracking-widest font-bold text-[10px] ${badgeStyles[st]}`}>
                  {st === "live" ? "● Live now" : st === "upcoming" ? "◐ Scheduled" : st === "expired" ? "◯ Expired" : "❚❚ Paused"}
                </span>
                {starts && st === "upcoming" && (
                  <span className="flex items-center gap-1 text-blue-600"><Calendar className="w-3 h-3"/> Starts {starts.toLocaleString()}</span>
                )}
                {ends && st !== "upcoming" && (
                  <span className={`flex items-center gap-1 ${st === "expired" ? "text-red-500" : "text-slate-500"}`}>
                    <Clock className="w-3 h-3"/> {st === "expired" ? "Ended " : "Ends "}{ends.toLocaleString()}
                  </span>
                )}
                <span className="text-slate-400 ml-auto">order: {d.order}</span>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div data-testid="deals-empty" className="col-span-full text-center py-12 text-slate-500">
            No deals in this status.
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={close}>
          <div data-testid="admin-deal-form"
               className="w-full max-w-2xl rounded-3xl bg-white dark:bg-slate-900 shadow-2xl p-8 max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-blue-950 dark:text-white">{editing.id ? "Edit Deal" : "New Deal"}</h3>
              <button onClick={close} className="w-9 h-9 rounded-full hover:bg-slate-100 dark:hover:bg-white/5 flex items-center justify-center"><X className="w-4 h-4"/></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Tag (small badge)" value={editing.tag} onChange={setF("tag")} placeholder="e.g. Monsoon Special" testid="deal-tag"/>
              <Field label="Package Slug" value={editing.slug} onChange={setF("slug")} placeholder="kerala" testid="deal-slug"/>
              <Field label="Title" value={editing.title} onChange={setF("title")} placeholder="Kerala Backwater Bliss" testid="deal-title" className="sm:col-span-2"/>
              <Field label="Deal line" value={editing.line} onChange={setF("line")} placeholder="From ₹19,999 · 6D/5N" testid="deal-line" className="sm:col-span-2"/>
              <Field label="CTA text" value={editing.cta} onChange={setF("cta")} placeholder="Book Now" testid="deal-cta"/>
              <Field label="Order" type="number" value={editing.order} onChange={setF("order")} testid="deal-order"/>
              <Field label="Colour (hex)" value={editing.color} onChange={setF("color")} placeholder="#D4AF37" testid="deal-color"/>
              <label className="flex items-center gap-2 mt-6 text-sm">
                <input type="checkbox" data-testid="deal-active" checked={editing.active} onChange={setF("active")} className="w-4 h-4"/>
                Active
              </label>
              <Field label="Start at (optional)" type="datetime-local" value={editing.start_at} onChange={setF("start_at")} testid="deal-start"/>
              <Field label="End at (drives countdown)" type="datetime-local" value={editing.end_at} onChange={setF("end_at")} testid="deal-end"/>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={close} className="px-6 py-3 rounded-full border border-slate-200 dark:border-white/10 text-sm">Cancel</button>
              <button data-testid="deal-save" disabled={saving} onClick={save} className="px-6 py-3 rounded-full btn-gold text-sm">
                {saving ? "Saving..." : (editing.id ? "Save changes" : "Create deal")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, testid, className = "", ...rest }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-widest text-slate-500">{label}</span>
      <input data-testid={testid} {...rest}
        className="mt-1 w-full rounded-2xl border border-slate-200 dark:border-white/10 bg-transparent px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"/>
    </label>
  );
}
